import { observer } from "mobx-react-lite";
import { useTranslations } from "next-intl";

import {
  flipMatrix,
  preserveCursorPosition,
  rotate,
  transpose,
  hasEqualMatrixBounds,
  toIdentityMatrix,
} from "@/components/expression/utils/matrix";
import { Icon } from "@/components/icons/Icon";
import { Sidebar } from "@/components/sidebar";
import { Dropdown } from "@/components/sidebar/Dropdown";
import { SidebarToolbar } from "@/components/sidebar/SidebarToolbar";

import type { GroupNodeModel } from "@/models/nodes/GroupNodeModel";

type Props = {
  node: GroupNodeModel;
};

export const GroupActions = observer(function GroupActions({ node }: Props) {
  const { matrixNode } = node;
  const t = useTranslations("sidebar");

  if (!matrixNode) {
    return null;
  }

  const items = [
    {
      title: t("activeNode.group.actions.transpose"),
      icon: <Icon name="math/matrix/Transpose" />,
      include: matrixNode.rows > 1 || matrixNode.cols > 1,
      onClick: () => {
        preserveCursorPosition(matrixNode, () => {
          transpose(matrixNode);
        });
      },
    },
    {
      title: t("activeNode.group.actions.flipHorizontal"),
      icon: <Icon name="math/matrix/FlipHorizontal" />,
      include: matrixNode.cols > 1,
      onClick: () => {
        preserveCursorPosition(matrixNode, () => {
          flipMatrix(matrixNode, "horizontal");
        });
      },
    },
    {
      title: t("activeNode.group.actions.flipVertical"),
      icon: <Icon name="math/matrix/FlipVertical" />,
      include: matrixNode.rows > 1,
      onClick: () => {
        preserveCursorPosition(matrixNode, () => {
          flipMatrix(matrixNode, "vertical");
        });
      },
    },
    {
      title: t("activeNode.group.actions.rotateLeft"),
      icon: <Icon name="math/matrix/RotateLeft" />,
      include: matrixNode.rows > 1 || matrixNode.cols > 1,
      onClick: () => {
        preserveCursorPosition(matrixNode, () => {
          rotate(matrixNode, "left");
        });
      },
    },
    {
      title: t("activeNode.group.actions.rotateRight"),
      icon: <Icon name="math/matrix/RotateRight" />,
      include: matrixNode.rows > 1 || matrixNode.cols > 1,
      onClick: () => {
        preserveCursorPosition(matrixNode, () => {
          rotate(matrixNode, "right");
        });
      },
    },
    {
      title: t("activeNode.group.actions.identity"),
      icon: <Icon name="math/matrix/IdentityMatrix" />,
      include: hasEqualMatrixBounds(matrixNode.bounds),
      onClick: () => {
        preserveCursorPosition(matrixNode, () => {
          toIdentityMatrix(matrixNode);
        });
      },
    },
  ].filter((item) => item.include);

  const MAX_VISIBLE = 6;
  const cutoff = items.length > MAX_VISIBLE ? MAX_VISIBLE - 1 : MAX_VISIBLE;
  const itemsOnPanel = items.slice(0, cutoff);
  const itemsInDropdown = items.slice(cutoff);

  return (
    <SidebarToolbar label={t("toolbars.groupActions")} className="gap-1.5">
      {itemsOnPanel.map((item) => (
        <Sidebar.Button
          key={item.title}
          icon={item.icon}
          title={item.title}
          onClick={item.onClick}
          className="h-8.5 min-h-8.5"
          inverted
        />
      ))}
      {itemsInDropdown.length > 0 && (
        <Dropdown
          toggleButtonComponent={
            <Sidebar.Button
              as="div"
              title={t("textStyle.more")}
              className="h-8.5 min-h-8.5 min-w-9"
              icon={<Icon name="ChevronDown" />}
              inverted
            />
          }
          items={itemsInDropdown}
        />
      )}
    </SidebarToolbar>
  );
});
